<template>
    <div v-if="!state.orderComplete">
        <div class="flex flex-col xl:gap-48 lg:gap-32 lg:flex-row-reverse lg:items-start lg:justify-between">
            <OrderDetails class="lg:flex-1" />

            <div class="col lg:flex-1">
                <AppAccordion
                    label="Add your email"
                    prefix="1."
                    :complete="store.$state.accountValidated"
                    v-model="store.$state.steps.createAccount.open"
                    ref="createAccountAccordion"
                >
                    <p class="my-4">We need this information to send you the gift code and order confirmation.</p>
                    <EmailStep />
                </AppAccordion>

                <AppAccordion
                    label="Select Equipment"
                    prefix="2."
                    class="border-t-2 border-gray-400"
                    :disabled="!store.$state.steps.createAccount.complete"
                    :complete="store.$state.steps.selectEquipment.complete"
                    v-model="store.$state.steps.selectEquipment.open"
                >
                    <EquipmentStep />
                </AppAccordion>

                <AppAccordion
                    :label="cart.requireShipping ? 'Shipping &amp; Payment' : 'Payment'"
                    prefix="3."
                    class="border-t-2 border-gray-400"
                    :disabled="!store.$state.steps.selectEquipment.complete"
                    :complete="store.$state.steps.paymentMethod.complete"
                    v-model="store.$state.steps.paymentMethod.open"
                >
                    <StripeElements
                        v-if="store.$state.steps.paymentMethod.open"
                        :handler="paymentHandler"
                        :validatorFn="prePaymentValidator"
                        :paymentIntentFn="paymentIntentFn"
                    />
                </AppAccordion>
            </div>
        </div>

        <ProgressModal
            v-if="state.showProgressModal"
            :steps="steps"
            :loading="state.showProgressModal"
        >
        </ProgressModal>
    </div>
    <div v-else-if="state.orderComplete && store.$state.giftOrder?.promo_code">
        <GiftPurchaseComplete :giftCode="store.$state.giftOrder?.promo_code" />
    </div>
</template>

<script setup lang="ts">
import { useCheckoutStore } from '@/store/checkout'
import { useCartStore } from '@/store/cart'
import OrderDetails from '@/components/Shared/OrderDetails.vue'
import EmailStep from '@/components/Shared/EmailStep.vue'
import EquipmentStep from '@/components/Shared/EquipmentStep.vue'
import AppAccordion from '@/components/App/AppAccordion.vue'
import { createHardwareOrder, isValidEmail } from '@/api/memberships'
import { createGiftIntent } from '@/api/gift'
import { computed, reactive, ref, nextTick } from 'vue'
import GiftPurchaseComplete from '@/components/Checkout/GiftPurchaseComplete.vue'
import { trackOrder } from '@/utils/trackers'
import StripeElements from '@/components/Shared/StripeElements.vue'
import ProgressModal from '@/components/Shared/ProgressModal.vue'

const store = useCheckoutStore()
const cart = useCartStore()
const state = reactive({
    paymentComplete: false,
    subscriptionComplete: false,
    equipmentComplete: false,
    trackingComplete: false,
    finalizingComplete: false,
    orderComplete: false,
    trackingResponse: {
        tracked: [] as any[],
        errors: [] as unknown[],
    },
    redirectInterval: null as number | null,
    showProgressModal: false,
})

const createAccountAccordion = ref()

const showEquipmentOrderStep = computed(() => cart.requireShipping)

const steps = computed((): ProgressStep[] => {
    const steps: ProgressStep[] = [
        {
            name: 'Verifying payment',
            status: state.paymentComplete ? 'complete' : 'current',
        },
        {
            name: 'Processing order',
            status: !state.paymentComplete
                ? 'upcoming'
                : state.subscriptionComplete
                    ? 'complete'
                    : 'current',
        },
        {
            name: 'Creating gift code',
            status: !state.equipmentComplete
                ? 'upcoming'
                : state.trackingComplete
                    ? 'complete'
                    : 'current',
        },
        {
            name: 'Order complete',
            status: !state.finalizingComplete
                ? 'upcoming'
                : state.orderComplete
                    ? 'complete'
                    : 'current',
        },
    ]
    if (showEquipmentOrderStep.value) {
        steps.splice(2, 0, {
            name: 'Ordering equipment',
            status: !state.subscriptionComplete
                ? 'upcoming'
                : state.equipmentComplete
                    ? 'complete'
                    : 'current',
        })
    }
    return steps
})

const handleTracking = (membershipOrder: any, equipmentOrder: any) => {
    const { tracked, errors } = trackOrder(membershipOrder, equipmentOrder)
    state.trackingResponse.tracked = tracked || []
    state.trackingResponse.errors = errors || []
}

const resetProgress = () => {
    state.paymentComplete = false
    state.equipmentComplete = false
    state.trackingComplete = false
    state.finalizingComplete = false
    state.orderComplete = false
    state.trackingResponse = {
        tracked: [] as any[],
        errors: [] as unknown[],
    }
    state.redirectInterval = null
    state.showProgressModal = false
}

const finalizeOrder = () => new Promise((resolve) => {
    setTimeout(() => {
        state.trackingComplete = true
        setTimeout(() => {
            state.finalizingComplete = true
            setTimeout(() => {
                state.orderComplete = true
                state.showProgressModal = false
                nextTick().then(() => {
                    window.scrollTo(0, 0)
                })
                resolve(true)
            }, 350)
        }, 250)
    }, 150)
})

const handleEquipmentPurchase = async(paymentMethodId: string, stripeCustomerId: string) => {
    const params = store.getCheckoutParams()
    const orderParams = {
        ...params,
        payment_method: paymentMethodId || store.$state.paymentMethodId,
        customer_id: stripeCustomerId || store.$state.stripeCustomerId,
        account_id: store.$state.accountId || undefined,
    }
    const equipmentOrder: EquipmentOrder = await createHardwareOrder(orderParams)
    state.equipmentComplete = true
    store.$patch({
        equipmentOrder,
    })
    return equipmentOrder
}

const createGiftIntentParams = (mergeParams: Partial<GiftIntentParams>): GiftIntentParams => {
    const batAttachment = cart.selectedHardware.find((hardware) => hardware.category === 'bat_attachment')
    return {
        name: store.$state.fullName || undefined,
        phone: store.$state.phone || undefined,
        gifter_email: store.$state.email || undefined,
        subscription: cart.selectedPlan?.stripe_price_id || '',
        bat_attachment_price: batAttachment && batAttachment.stripe_price_id,
        address: store.$state.billingAddress.postal_code ? store.$state.billingAddress : undefined,
        shipping: store.$state.shippingAddress.postal_code ? {
            name: store.$state.fullName || undefined,
            phone: store.$state.phone || undefined,
            address: store.$state.shippingAddress,
        } : undefined,
        payment_intent_id: store.$state.paymentIntent?.id || undefined,
        coupon_code: cart.$state.couponPromo?.code,
        ...mergeParams,
    }
}

const paymentIntentFn = async(mergeParams?: Partial<GiftIntentParams>) => {
    const response: GiftIntentResponse = await createGiftIntent(createGiftIntentParams(mergeParams || {}))
    store.$patch({
        paymentIntent: {
            id: response.payment_intent,
            client_secret: response.payment_intent_secret,
        },
    })
}

const handleGiftPurchase = async(paymentMethodId: string): Promise<GiftIntentResponse> => {
    if (paymentMethodId) {
        store.$patch({
            paymentMethodId: paymentMethodId || store.$state.paymentMethodId,
        })
    }
    const orderParams: GiftIntentParams = createGiftIntentParams({
        payment_method: store.$state.paymentMethodId,
    })

    const giftOrder: GiftIntentResponse = await createGiftIntent(orderParams)
    store.$patch({
        giftOrder,
        membershipPurchaseComplete: true,
        stripeCustomerId: giftOrder.stripe_customer_id,
    })

    try {
        localStorage.setItem('wr_gift_data', JSON.stringify(giftOrder))
    } catch (error) {
        console.error(error)
    }

    state.subscriptionComplete = true
    if (!showEquipmentOrderStep.value) {
        state.equipmentComplete = true
    }
    return giftOrder
}

const prePaymentValidator = async(): Promise<boolean> => {
    const isValid = await isValidEmail(store.$state.email)
    if (!isValid) {
        window.scrollTo({
            top: createAccountAccordion.value.$el.offsetTop,
            behavior: 'smooth',
        })
        throw new Error('Your email address is invalid')
    }
    return true
}

const paymentHandler = async(paymentMethodId: string) => {
    state.showProgressModal = true
    setTimeout(() => {
        state.paymentComplete = true
    }, 500)
    try {
        if (!store.$state.giftOrder) {
            await handleGiftPurchase(paymentMethodId)
        }

        if (cart.requireShipping && store.$state.giftOrder?.stripe_customer_id) {
            const stripeCustomerId = store.$state.giftOrder.stripe_customer_id
            await handleEquipmentPurchase(
                paymentMethodId || store.$state.paymentMethodId,
                stripeCustomerId,
            )
        }
        handleTracking(store.$state.giftOrder, store.$state.equipmentOrder)
        await finalizeOrder()
    } catch (e: any) {
        resetProgress()
        if (e?.message && typeof e.message === 'string') {
            try {
                const message = JSON.parse(e.message || '{}')
                if (message?.message) {
                    throw new Error(message.message)
                }
            } catch (error) {
                //throw e
            }
        } else if (e?.message && typeof e.message !== 'string') {
            console.error(e)
            throw new Error('Something went wrong. Please contact support')
        }
        throw e
    }
}

</script>
